// ============================================================
// CANAL INFLUENCER — el estado que viaja entre pantallas (s186)
// ============================================================
// Diseño: `claude/SPEC_s185_flujo_enganche.md` · prompt de esta tanda:
// `PROMPT_CODE_s185_landing_influencer_y_enganche.md`.
//
// La persona escribe UN código. Por detrás son dos objetos: el `AccessCode` de
// la influencer la mete a la plataforma (LITE con 2 activaciones gratis) y trae
// colgado el código de ENGANCHE — un `DiscountCode` de precio fijo amarrado al
// plan de 6 meses. El segundo código no se le pide nunca: se aplica solo en el
// checkout. Este módulo es el que lo lleva de una pantalla a la otra.
//
// Dos tramos, dos llaves:
//   `nxt_invita`   — el código del canal, desde la landing /i/:codigo hasta la
//                    puerta /onboarding. Existe ANTES de que haya cuenta, así
//                    que no se puede guardar en el usuario: vive en el browser.
//   `nxt_enganche` — lo que devolvió `apply_access_code`. Existe DESPUÉS del
//                    login, y por eso va firmado con el user id (abajo).
//
// 🔑 POR QUÉ EL ENGANCHE VA FIRMADO CON EL USER ID
// localStorage es del navegador, no de la sesión. Sin firma, la oferta de
// enganche de quien entró por el link de una influencer sobreviviría a un
// logout y se le ofrecería —con su precio— a la persona que entre después en
// esa misma computadora. Es el patrón de fuga entre clientes que ya nos costó
// una investigación en el otro proyecto. `leerEnganche()` devuelve null cuando
// la firma no coincide con la sesión de ahora, y de paso limpia el rastro.
//
// TODO EL MÓDULO ES DEFENSIVO CONTRA `localStorage`: en Safari privado y con
// las cookies de sitio bloqueadas, `getItem` TIRA. Una landing no se puede caer
// por no poder recordar un código.

import { auth } from "./api";

const K_INVITA = "nxt_invita";
const K_ENGANCHE = "nxt_enganche";

function leerCrudo(clave) {
  try { return localStorage.getItem(clave); } catch (e) { return null; }
}
function escribirCrudo(clave, valor) {
  try { localStorage.setItem(clave, valor); return true; } catch (e) { return false; }
}
function borrarCrudo(clave) {
  try { localStorage.removeItem(clave); } catch (e) { /* nada que hacer */ }
}

// Un código de canal es texto corto en mayúsculas. Se normaliza en UN lugar
// —acá— para que la landing, la puerta y el checkout comparen lo mismo.
export function normalizarCodigo(codigo) {
  return String(codigo || "").trim().toUpperCase().slice(0, 40);
}

// ---- TRAMO 1 · el código del canal (pre-cuenta) ----------------------------

export function guardarCodigoInvitacion(codigo) {
  const limpio = normalizarCodigo(codigo);
  if (!limpio) return false;
  return escribirCrudo(K_INVITA, limpio);
}

export function leerCodigoInvitacion() {
  return normalizarCodigo(leerCrudo(K_INVITA));
}

export function limpiarCodigoInvitacion() {
  borrarCrudo(K_INVITA);
}

// ---- TRAMO 2 · el enganche que devolvió apply_access_code ------------------

// ⚠️ CONTRATO EN CONSTRUCCIÓN. En el export 75 —que ES la rama Main, no solo
// producción— el Return final de `apply_access_code` devuelve exactamente tres
// cosas: `success`, `tier` y `vip_expires_at`. `enganche_code` existe como
// campo del `AccessCode` y como param de `create_access_code` (17), o sea que
// se puede ESCRIBIR, pero todavía no hay quien lo lea de vuelta; y
// `enganche_plan_id` no existe en ningún lado (ni campo, ni param, ni Return).
// Es el bloque D de la spec, sin construir.
//
// Consecuencia práctica: hoy esto SIEMPRE devuelve `false` y el producto se
// comporta igual que siempre. El día que el Return traiga los campos, el canal
// se enciende solo, sin tocar React. Por eso se lee tolerante (dos nombres
// posibles para el plan) y por eso el plan es OPCIONAL: con `enganche_code`
// solo, el front resuelve el plan de 6 meses por su duración.
export function guardarEngancheDesdeRespuesta(resp) {
  const code = normalizarCodigo(resp?.enganche_code);
  if (!code) return false;
  const planId = String(resp?.enganche_plan_id || resp?.enganche_plan || "").trim();
  const uid = auth.getUserId() || "";
  return escribirCrudo(K_ENGANCHE, JSON.stringify({ code, planId, uid }));
}

// Devuelve { code, planId } o null. Null también cuando el enganche quedó de
// otra sesión: ver la nota de la firma, arriba.
export function leerEnganche() {
  const crudo = leerCrudo(K_ENGANCHE);
  if (!crudo) return null;
  let dato = null;
  try { dato = JSON.parse(crudo); } catch (e) { borrarCrudo(K_ENGANCHE); return null; }
  if (!dato || !dato.code) { borrarCrudo(K_ENGANCHE); return null; }
  const uid = auth.getUserId() || "";
  // Sin sesión no se ofrece nada; con OTRA sesión, se borra: el enganche es de
  // la persona que aplicó el código, no de la computadora.
  if (!uid) return null;
  if (dato.uid && dato.uid !== uid) { borrarCrudo(K_ENGANCHE); return null; }
  return { code: dato.code, planId: dato.planId || "" };
}

export function limpiarEnganche() {
  borrarCrudo(K_ENGANCHE);
}

// ---- El plan del enganche --------------------------------------------------

// s185-D7: la duración sale del plan de 6 meses que YA existe (lista $135,
// activo y no exhibido). Se elige por id cuando el backend lo mande; mientras
// tanto, por duración — que es el dato que define la ola, no el nombre.
// `planes` es lo que devuelve getMembershipPlans(); puede venir vacío (un
// anónimo NO puede leer MembershipPlan: la regla `everyone` está toda apagada,
// verificado con GET /obj/membershipplan sin token → count 0).
export const MESES_ENGANCHE = 6;

export function elegirPlanEnganche(planes, planId) {
  const lista = Array.isArray(planes) ? planes : [];
  if (planId) {
    const porId = lista.find((p) => p && p._id === planId);
    if (porId) return porId;
  }
  const porDuracion = lista.find((p) => Number(p?.duration_months) === MESES_ENGANCHE);
  return porDuracion || null;
}
